import { apiFetch } from "@/lib/api";
import type { RegistrationDto } from "@/lib/types";
import { useSession } from "./session-provider";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface RegistrationsState {
  /** Active and completed registrations used by participant navigation. */
  registrations: RegistrationDto[];
  /** Includes withdrawn registrations for rejoin and registration management. */
  allRegistrations: RegistrationDto[];
  /** The challenge the nav and bottom bar link to. */
  activeSlug: string | null;
  loading: boolean;
  /**
   * True once the list is a settled answer for the signed-in user, rather than
   * the empty array this provider starts with. Anything that treats "no
   * registrations" as a fact — redirecting a first-time user, for instance —
   * must wait for this, because `loading` is false both before the first fetch
   * starts and after it finishes.
   */
  loaded: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  /**
   * Applies a freshly logged total to the cached registration. Logging activity
   * happens on the tracker, but the dashboard hero and challenge cards read
   * their numbers from here — without this they keep rendering the snapshot
   * taken when the app loaded and appear stuck at the pre-activity total.
   */
  applyProgress: (
    challengeId: string,
    progress: { totalDistance: number; currentStreak: number },
  ) => void;
}

const RegistrationsContext = createContext<RegistrationsState>({
  registrations: [],
  allRegistrations: [],
  activeSlug: null,
  loading: false,
  loaded: false,
  error: null,
  refresh: async () => {},
  applyProgress: () => {},
});

/**
 * Fetches the signed-in user's registrations once for the whole app so the
 * sidebar, bottom bar and pages share a single request rather than each
 * mounting their own `useApi("/api/registrations")`.
 */
export function RegistrationsProvider({ children }: { children: ReactNode }) {
  const { me, loading: sessionLoading } = useSession();
  const authenticated = me?.authenticated === true;
  const userId = me?.id ?? null;

  const [registrations, setRegistrations] = useState<RegistrationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Which user the current list belongs to, so signing out and back in as
  // someone else cannot be mistaken for settled data about the new user.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!authenticated) {
      setRegistrations([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<RegistrationDto[]>(
        "/api/registrations?includeWithdrawn=true",
      );
      setRegistrations(data ?? []);
    } catch (requestError) {
      // A failed registrations read must never break the shell — the nav
      // simply hides its challenge-scoped links.
      setRegistrations([]);
      setError(requestError);
    } finally {
      setLoading(false);
      setLoadedFor(userId);
    }
  }, [authenticated, userId]);

  const applyProgress = useCallback(
    (
      challengeId: string,
      progress: { totalDistance: number; currentStreak: number },
    ) => {
      setRegistrations((prev) =>
        prev.map((registration) =>
          registration.challengeId === challengeId
            ? {
                ...registration,
                totalDistance: progress.totalDistance,
                currentStreak: progress.currentStreak,
              }
            : registration,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<RegistrationsState>(
    () => {
      const activeRegistrations = registrations.filter(
        registration => registration.status !== "Withdrawn",
      );
      return {
        registrations: activeRegistrations,
        allRegistrations: registrations,
        activeSlug: pickActive(activeRegistrations),
        loading,
        // A signed-out visitor has a settled answer as soon as the session is
        // known: no registrations. A signed-in one needs their own fetch back.
        loaded: !sessionLoading && (!authenticated || loadedFor === userId),
        error,
        refresh,
        applyProgress,
      };
    },
    [
      registrations,
      loading,
      sessionLoading,
      authenticated,
      loadedFor,
      userId,
      error,
      refresh,
      applyProgress,
    ],
  );

  return (
    <RegistrationsContext.Provider value={value}>
      {children}
    </RegistrationsContext.Provider>
  );
}

export function useRegistrations() {
  return useContext(RegistrationsContext);
}

/**
 * Someone in several challenges almost always means the one running right now,
 * so that is what the nav opens. Registrations arrive newest-first, which is the
 * fallback once every challenge has finished.
 */
function pickActive(registrations: RegistrationDto[]): string | null {
  const running = registrations.find(
    (r) =>
      r.challengeStartDate.slice(0, 10) <= r.challengeCurrentDate &&
      r.challengeCurrentDate <= r.challengeEndDate.slice(0, 10),
  );
  return (running ?? registrations[0])?.challengeSlug ?? null;
}
